import React, { useEffect, useState } from 'react';
import Uppy from '@uppy/core';
import { Dashboard } from '@uppy/react';
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import { useTranslation } from 'react-i18next';

/**
 * Implements a file uploader using Uppy. Uppy can also directly upload files from Google Drive, MS Drive, etc.
 * It requires some custom backend authentication and the Uppy companion server. Uppy Companion is open-source.
 */
const FileUploader = /*#__PURE__*/React.memo(_ref => {
  let {
    onSelectFile,
    setUppyInstance,
    onUserEditFileStart,
    onUserEditFileEnd
  } = _ref;
  const {
    t
  } = useTranslation();
  // Use useState for restrictions since it doesn't change
  const [restrictions] = useState({
    maxFileSize: 500 * 1024 * 1024,
    allowedFileTypes: null
  });
  const [uppy, setUppy] = useState(null);
  useEffect(() => {
    const uppyInstance = new Uppy({
      restrictions,
      autoProceed: false,
      // Set to false to manually control uploads
      debug: true,
      logger: {
        debug: message => console.log(`Uppy debug: ${message}`),
        warn: message => console.warn(`Uppy warn: ${message}`),
        error: message => console.error(`Uppy error: ${message}`)
      }
    });
    uppyInstance.on('file-added', file => {
      onSelectFile(file);
    });

    // These event setups are left as examples.
    // uppyInstance.on('upload-success', (file) => {
    //   console.log('Cloud and backend upload success for file:', file.name)
    // })
    // uppyInstance.on('upload-error', (file, error, response) => {
    //   console.error('Upload error for file:', file.name)
    // })
    // uppyInstance.on('complete', (result) => {
    //   console.log('Upload complete. Successful files:', result.successful)
    // })

    /**
     * This method should not be used when using chunking upload with AWS.
     * When using chunking upload - we can automatically track the progress
     * by providing the bites uploaded vs the total bytes like so:
     * 
     * uppyInstance.emit('upload-progress', file, {
     *   uploader: uppyInstance,
     *   bytesUploaded: file.size,
     *   bytesTotal: file.size
     * })
     */
    uppyInstance.on('manual-progress-update', (file, percentage) => {
      const updatedFiles = Object.assign({}, uppyInstance.getState().files);
      const updatedFile = Object.assign({}, updatedFiles[file.id], {
        progress: Object.assign({}, updatedFiles[file.id].progress, {
          uploadComplete: false,
          uploadStarted: true,
          percentage: percentage
        })
      });
      updatedFiles[file.id] = updatedFile;
      uppyInstance.setState({
        files: updatedFiles
      });
    });
    uppyInstance.on('dashboard:file-edit-start', file => {
      onUserEditFileStart(file);
    });
    uppyInstance.on('file-meta-updated', file => {
      const newName = file.meta.name;
      if (newName && newName !== file.name) {
        uppyInstance.setFileState(file.id, {
          name: newName // override the internal file name Uppy shows in the UI
        });
      }
    });
    uppyInstance.on('dashboard:file-edit-complete', file => {
      onUserEditFileEnd(file);
    });

    /**
     * Passing uppy instances up in the state tree - should only be required when using custom GCP implementation.
     * This is required as there is additional logic for signing urls, custom upload logic, etc.
     */
    setUppy(uppyInstance);
    setUppyInstance(uppyInstance);
    return () => {
      uppyInstance.cancelAll();
    };
  }, [onSelectFile, onUserEditFileEnd, onUserEditFileStart, restrictions, setUppyInstance]); // Only depend on restrictions which doesn't change

  if (!uppy) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Dashboard, {
    uppy: uppy,
    proudlyDisplayPoweredByUppy: false,
    showProgressDetails: false // TODO: set to true  AWS MIGRATION and implementing the necessary changes for chunked upload
    ,
    hideUploadButton: true,
    metaFields: [{
      id: 'name',
      name: t('name'),
      placeholder: 'File name'
    }, {
      id: 'notes',
      name: t('notes'),
      placeholder: 'Notes about the file'
    }]
  });
});
export default FileUploader;